/**
 * BboxOverlayModal — overlays the bounding boxes from an `extract_visuals`
 * run on top of the student's solution image, so an admin can spot when the
 * model's segment boxes are wrong.
 *
 * Coordinate system: each segment's `bbox` is `[x, y, width, height]`,
 * NORMALIZED to 0–1 against the exact image that was sent to the LLM (the same
 * image both detail pages already display). Because the coordinates are
 * normalized, the overlay is pure CSS percentages — no image pixel dimensions
 * needed, and it stays aligned at any rendered size.
 *
 * See Backend/functions/src/prompt/typeSchemas/extractVisuals.ts (bbox schema)
 * and the extract_visuals prompt's "each between 0 and 1, normalized to the
 * image dimensions" instruction.
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface BboxSegment {
    segment_id?: string;
    segment_type?: string;
    bbox?: number[];
    crossed_out?: boolean;
    literal_text?: string;
    semantic_description?: string;
}

interface Props {
    imageUrl: string;
    segments: BboxSegment[];
    onClose: () => void;
}

/** A segment is drawable iff its bbox is a 4-number array. */
function hasValidBbox(s: BboxSegment): s is BboxSegment & { bbox: number[] } {
    return Array.isArray(s.bbox) && s.bbox.length === 4 && s.bbox.every(n => typeof n === 'number');
}

/** Stable label for a segment, falling back to positional index. */
function segLabel(s: BboxSegment, i: number): string {
    return s.segment_id ?? `s${i + 1}`;
}

export function BboxOverlayModal({ imageUrl, segments, onClose }: Props) {
    // Index of the segment currently highlighted (hover on box or legend row).
    const [activeIdx, setActiveIdx] = useState<number | null>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    const drawable = segments
        .map((s, i) => ({ s, i }))
        .filter(({ s }) => hasValidBbox(s));

    return createPortal(
        <div className="bbox-overlay-backdrop" onClick={onClose}>
            <div className="bbox-overlay-modal" onClick={e => e.stopPropagation()}>
                <div className="bbox-overlay-header">
                    <h2>Bounding boxes on student solution</h2>
                    <button className="bbox-overlay-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="bbox-overlay-body">
                    <div className="bbox-overlay-stage">
                        <img src={imageUrl} alt="Student solution" className="bbox-overlay-img" draggable={false} />
                        {drawable.map(({ s, i }) => {
                            const [x, y, w, h] = (s as BboxSegment & { bbox: number[] }).bbox;
                            const active = activeIdx === i;
                            return (
                                <div
                                    key={segLabel(s, i)}
                                    className={`bbox-rect${s.crossed_out ? ' crossed-out' : ''}${active ? ' active' : ''}`}
                                    style={{
                                        left: `${x * 100}%`,
                                        top: `${y * 100}%`,
                                        width: `${w * 100}%`,
                                        height: `${h * 100}%`,
                                    }}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    onMouseLeave={() => setActiveIdx(null)}
                                >
                                    <span className="bbox-rect-label">{segLabel(s, i)}</span>
                                </div>
                            );
                        })}
                    </div>
                    <div className="bbox-overlay-legend">
                        <div className="bbox-overlay-legend-head">
                            {drawable.length} of {segments.length} segment{segments.length === 1 ? '' : 's'} with a box
                        </div>
                        <ul>
                            {drawable.map(({ s, i }) => (
                                <li
                                    key={segLabel(s, i)}
                                    className={`${activeIdx === i ? 'active' : ''}${s.crossed_out ? ' crossed-out' : ''}`}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    onMouseLeave={() => setActiveIdx(null)}
                                >
                                    <span className="bbox-legend-id">{segLabel(s, i)}</span>
                                    {s.segment_type && <span className="bbox-legend-type">{s.segment_type}</span>}
                                    {s.crossed_out && <span className="bbox-legend-crossed">crossed out</span>}
                                    {(s.literal_text || s.semantic_description) && (
                                        <span className="bbox-legend-text">
                                            {s.literal_text || s.semantic_description}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}
